import React, { useEffect, useState } from "react";
import axios from "axios";
import { Wand2, CheckCircle, AlertTriangle, Users, FileText, Building2, RefreshCw, Zap, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

function StatusCard({ title, count, icon: Icon, color, description }) {
  const colors = {
    red: "border-red-200 bg-red-50 text-red-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    teal: "border-teal-200 bg-teal-50 text-teal-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
  };
  
  return (
    <div className={`rounded-2xl border p-4 ${colors[color]}`}>
      <div className="flex items-center gap-3">
        <Icon className="w-8 h-8" />
        <div>
          <div className="text-2xl font-bold">{count}</div>
          <div className="text-sm font-medium">{title}</div>
        </div>
      </div>
      {description && <div className="mt-2 text-xs opacity-75">{description}</div>}
    </div>
  );
}

function FixSection({ title, icon: Icon, count, children, onFix, fixing, fixLabel }) {
  const [expanded, setExpanded] = useState(count > 0);
  
  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div 
        className="flex items-center justify-between p-5 cursor-pointer hover:bg-slate-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-6 h-6 ${count > 0 ? 'text-amber-600' : 'text-emerald-600'}`} />
          <div>
            <div className="text-lg font-semibold text-slate-800">{title}</div>
            <div className={`text-sm ${count > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
              {count > 0 ? `${count} issues to fix` : 'No issues'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {count > 0 && onFix && (
            <button
              onClick={(e) => { e.stopPropagation(); onFix(); }}
              disabled={fixing}
              className="rounded-full bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:bg-slate-300 transition flex items-center gap-2"
            >
              <Zap className={`w-4 h-4 ${fixing ? 'animate-pulse' : ''}`} />
              {fixing ? 'Fixing...' : fixLabel || 'Fix All'}
            </button>
          )}
          <ChevronRight className={`w-5 h-5 text-slate-400 transition ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </div>
      
      {expanded && count > 0 && (
        <div className="border-t border-slate-200 p-5 bg-slate-50">
          {children}
        </div>
      )}
    </div>
  );
}

export default function QuickFixWizardPage() {
  const [status, setStatus] = useState(null);
  const [orphans, setOrphans] = useState([]);
  const [missing, setMissing] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [parks, setParks] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPark, setSelectedPark] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [fixingOrphans, setFixingOrphans] = useState(false);
  const [fixingMissing, setFixingMissing] = useState(false);
  const [mergingDuplicates, setMergingDuplicates] = useState(false);
  const [autoFixing, setAutoFixing] = useState(false);
  const [liveAudit, setLiveAudit] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, o, m, d, p, c, a] = await Promise.all([
        axios.get(`${API}/api/admin/quick-fix/status`),
        axios.get(`${API}/api/admin/quick-fix/orphan-jobs`),
        axios.get(`${API}/api/admin/quick-fix/missing-fields`),
        axios.get(`${API}/api/admin/quick-fix/duplicate-contractors`),
        axios.get(`${API}/api/admin/quick-fix/available-parks`),
        axios.get(`${API}/api/admin/quick-fix/available-clients`),
        axios.get(`${API}/api/admin/test-data/lockdown/latest-live`),
      ]);
      setStatus(s.data);
      setOrphans(o.data.jobs || []);
      setMissing(m.data.jobs || []);
      setDuplicates(d.data.groups || []);
      setParks(p.data.parks || []);
      setClients(c.data.clients || []);
      setLiveAudit(a.data);
      
      // Auto-select first park/client if available
      if (p.data.parks?.length > 0 && !selectedPark) {
        setSelectedPark(p.data.parks[0].id);
        if (p.data.parks[0].client_id) {
          setSelectedClient(p.data.parks[0].client_id);
        }
      }
    } catch (err) {
      console.error("Failed to load:", err);
    } finally {
      setLoading(false);
    }
  };

  const fixOrphans = async () => {
    if (!selectedPark) {
      alert("Please select a park first");
      return;
    }
    setFixingOrphans(true);
    try {
      const ids = orphans.map(j => j.job_id);
      await axios.post(`${API}/api/admin/quick-fix/fix-orphan-jobs`, {
        job_ids: ids,
        park_id: selectedPark,
        client_id: selectedClient || null
      });
      await load();
    } catch (err) {
      alert("Fix failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setFixingOrphans(false);
    }
  };

  const fixMissing = async () => {
    setFixingMissing(true);
    try {
      const fixes = missing.map(j => ({
        job_id: j.job_id,
        description: j.missing.includes("description") ? `Job ${j.job_reference || 'Unknown'} - requires attention` : undefined,
        issue_type: j.missing.includes("issue_type") ? "General Maintenance" : undefined,
        park_id: j.missing.includes("park_id") && selectedPark ? selectedPark : undefined,
      }));
      await axios.post(`${API}/api/admin/quick-fix/fix-missing-fields`, { fixes });
      await load();
    } catch (err) {
      alert("Fix failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setFixingMissing(false);
    }
  };

  const mergeDuplicates = async () => {
    setMergingDuplicates(true);
    try {
      const merges = duplicates.map(g => ({
        primary_id: g.suggested_primary,
        merge_ids: g.contractors.filter(c => c.id !== g.suggested_primary).map(c => c.id)
      }));
      await axios.post(`${API}/api/admin/quick-fix/merge-contractors`, { merges });
      await load();
    } catch (err) {
      alert("Merge failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setMergingDuplicates(false);
    }
  };

  const autoFixAll = async () => {
    if (!selectedPark) {
      alert("Please select a default park first");
      return;
    }
    setAutoFixing(true);
    try {
      await axios.post(`${API}/api/admin/quick-fix/auto-fix-all`, {
        default_park_id: selectedPark,
        default_client_id: selectedClient || null,
        default_issue_type: "General Maintenance",
        merge_duplicates: true
      });
      // Run live audit
      const auditRes = await axios.post(`${API}/api/admin/test-data/lockdown/run-live`);
      setLiveAudit(auditRes.data.audit);
      await load();
    } catch (err) {
      alert("Auto-fix failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setAutoFixing(false);
    }
  };

  const runLiveAudit = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/test-data/lockdown/run-live`);
      setLiveAudit(res.data.audit);
    } catch (err) {
      alert("Audit failed: " + (err.response?.data?.detail || err.message));
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal-600 border-t-transparent" />
      </div>
    );
  }

  const isGreen = status?.ready_for_green || liveAudit?.traffic === "GREEN";
  const trafficColors = {
    GREEN: "from-emerald-700 to-emerald-600",
    AMBER: "from-amber-600 to-amber-500",
    RED: "from-red-700 to-red-600",
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quick-fix-wizard-page">
      {/* Header */}
      <div className={`bg-gradient-to-r ${isGreen ? 'from-emerald-700 to-emerald-600' : 'from-teal-700 to-teal-600'} text-white`}>
        <div className="mx-auto max-w-5xl px-6 py-8">
          <div className="flex items-center gap-4">
            <Wand2 className="w-10 h-10" />
            <div>
              <h1 className="text-3xl font-semibold">Quick Fix Wizard</h1>
              <p className="mt-1 text-sm opacity-90">
                {isGreen ? "System is GREEN - Ready for Go-Live!" : "Resolve final blockers to reach GREEN status"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Status Summary */}
      <div className="mx-auto max-w-5xl px-6 py-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatusCard 
            title="Orphan Jobs" 
            count={status?.orphan_jobs || 0} 
            icon={Building2}
            color={status?.orphan_jobs > 0 ? "amber" : "green"}
            description="Missing park or client"
          />
          <StatusCard 
            title="Missing Fields" 
            count={status?.missing_fields || 0} 
            icon={FileText}
            color={status?.missing_fields > 0 ? "amber" : "green"}
            description="Missing required data"
          />
          <StatusCard 
            title="Duplicate Contractors" 
            count={status?.duplicate_contractors || 0} 
            icon={Users}
            color={status?.duplicate_contractors > 0 ? "amber" : "green"}
            description="Same email multiple times"
          />
          <StatusCard 
            title="Total Issues" 
            count={status?.total_issues || 0} 
            icon={status?.ready_for_green ? CheckCircle : AlertTriangle}
            color={status?.ready_for_green ? "green" : "red"}
            description={status?.ready_for_green ? "Ready for GREEN!" : "Fix to reach GREEN"}
          />
        </div>
      </div>

      {/* Live Audit Status */}
      {liveAudit && liveAudit.status !== "NO_AUDIT" && (
        <div className="mx-auto max-w-5xl px-6 pb-4">
          <div 
            className={`rounded-2xl bg-gradient-to-r ${trafficColors[liveAudit.traffic] || trafficColors.RED} p-4 text-white flex items-center justify-between`}
          >
            <div className="flex items-center gap-3">
              {liveAudit.traffic === "GREEN" ? (
                <CheckCircle className="w-8 h-8" />
              ) : (
                <AlertTriangle className="w-8 h-8" />
              )}
              <div>
                <div className="font-semibold">Live Audit: {liveAudit.traffic}</div>
                <div className="text-sm opacity-90">Score: {liveAudit.score}%</div>
              </div>
            </div>
            <button 
              onClick={runLiveAudit}
              className="rounded-full bg-white/20 px-4 py-2 text-sm font-medium hover:bg-white/30 transition"
            >
              Re-run Audit
            </button>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mx-auto max-w-5xl px-6 pb-4">
        <div className="rounded-2xl border border-teal-200 bg-teal-50 p-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-lg font-semibold text-teal-900">One-Click Fix</div>
              <div className="text-sm text-teal-700">Select default park, then auto-fix all issues</div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <select
                value={selectedPark}
                onChange={(e) => {
                  setSelectedPark(e.target.value);
                  const park = parks.find(p => p.id === e.target.value);
                  if (park?.client_id) setSelectedClient(park.client_id);
                }}
                className="rounded-xl border border-teal-300 bg-white px-4 py-2 text-sm"
                data-testid="park-select"
              >
                <option value="">Select Park</option>
                {parks.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="rounded-xl border border-teal-300 bg-white px-4 py-2 text-sm"
                data-testid="client-select"
              >
                <option value="">Select Client (Optional)</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <button
                onClick={autoFixAll}
                disabled={autoFixing || !selectedPark || status?.total_issues === 0}
                className="rounded-full bg-teal-600 px-6 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:bg-slate-300 transition flex items-center gap-2"
                data-testid="auto-fix-all-btn"
              >
                <Zap className={`w-4 h-4 ${autoFixing ? 'animate-pulse' : ''}`} />
                {autoFixing ? 'Fixing All...' : 'Auto-Fix All & Audit'}
              </button>
              <button
                onClick={load}
                className="rounded-full border border-teal-300 bg-white px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50 transition flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Fix Sections */}
      <div className="mx-auto max-w-5xl px-6 pb-8 space-y-4">
        {/* Orphan Jobs */}
        <FixSection 
          title="Orphan Jobs" 
          icon={Building2} 
          count={orphans.length}
          onFix={fixOrphans}
          fixing={fixingOrphans}
          fixLabel="Assign to Selected Park"
        >
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {orphans.map((j, i) => (
              <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 text-sm">
                <div className="font-medium text-slate-800">{j.job_reference}</div>
                <div className="text-slate-500 truncate">{j.description || 'No description'}</div>
                <div className="mt-1 flex gap-2">
                  {j.missing_park && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">Missing Park</span>}
                  {j.missing_client && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">Missing Client</span>}
                </div>
              </div>
            ))}
          </div>
        </FixSection>

        {/* Missing Fields */}
        <FixSection 
          title="Missing Required Fields" 
          icon={FileText} 
          count={missing.length}
          onFix={fixMissing}
          fixing={fixingMissing}
          fixLabel="Fill with Defaults"
        >
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {missing.map((j, i) => (
              <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 text-sm">
                <div className="font-medium text-slate-800">{j.job_reference}</div>
                <div className="mt-1 flex gap-2 flex-wrap">
                  {j.missing.map((m, idx) => (
                    <span key={idx} className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </FixSection>

        {/* Duplicate Contractors */}
        <FixSection 
          title="Duplicate Contractors" 
          icon={Users} 
          count={duplicates.length}
          onFix={mergeDuplicates}
          fixing={mergingDuplicates}
          fixLabel="Merge All (Keep Primary)"
        >
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {duplicates.map((g, i) => (
              <div key={i} className="rounded-xl bg-white border border-slate-200 p-3">
                <div className="text-sm font-medium text-slate-800 mb-2">
                  Email: {g.email} ({g.count} duplicates)
                </div>
                <div className="space-y-1">
                  {g.contractors.map((c, idx) => (
                    <div 
                      key={idx} 
                      className={`rounded-lg p-2 text-sm flex items-center justify-between ${
                        c.id === g.suggested_primary 
                          ? 'bg-teal-50 border border-teal-200' 
                          : 'bg-slate-50'
                      }`}
                    >
                      <div>
                        <div className="font-medium">{c.company_name || 'Unknown'}</div>
                        <div className="text-xs text-slate-500">
                          {c.job_count} jobs · {c.mobile || 'No phone'}
                        </div>
                      </div>
                      {c.id === g.suggested_primary && (
                        <span className="rounded-full bg-teal-100 px-2 py-0.5 text-xs text-teal-700 font-medium">
                          PRIMARY
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </FixSection>
      </div>

      {/* Success State */}
      {isGreen && (
        <div className="mx-auto max-w-5xl px-6 pb-8">
          <div className="rounded-3xl bg-gradient-to-r from-emerald-600 to-teal-600 p-8 text-white text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-2">System Ready for Go-Live!</h2>
            <p className="text-lg opacity-90">
              All live data issues have been resolved. The system is now at GREEN status.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
